import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Link from "next/link";
import { classes, createClasses } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";
import styles from "./style.module.css";
import Table from "~components/Table";
import TableColumn from "~components/Table/TableColumn";
import TableHeader from "~components/Table/TableHeader";
import TableBody from "~components/Table/TableBody";
import TableRow from "~components/Table/TableRow";
import TableCell from "~components/Table/TableCell";
import { addPageAction } from "~utils/newrelic/newrelicBrowser";
import { LocalizedText, TranslatedMessageType } from "~utils/i18n/translate";

import type {
  TableColumnDefinition,
  TableColumnSortConfig,
} from "~types/table";

type SectionFrontTableProps<T> = {
  columns: Array<TableColumnDefinition<T>>;
  rows: Array<T>;
  maxColumnsOnMobile: number;
  className?: string;
  title?: string | TranslatedMessageType;
  subtitle?: string | TranslatedMessageType;
  sectionHeader?: ReactNode;
  sectionUrl?: string;
  showHeaderControls?: boolean;
  disclaimer?: string;
};

const toNum = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const trimmed = value.trim();

    // this is to account for empty fields (ie. open) so that n/a values go to the end
    if (trimmed.toLowerCase() === "n/a") {
      return Number.POSITIVE_INFINITY;
    }
    const number = Number(trimmed.replace(/,/g, ""));

    return Number.isFinite(number) ? number : Number.POSITIVE_INFINITY;
  }

  return Number.POSITIVE_INFINITY;
};

const toMinutesSinceMidnight = (value: unknown): number => {
  if (typeof value !== "string") return NaN;
  const timeParts = value.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (!timeParts) return NaN;
  let hours = parseInt(timeParts[1], 10);
  const minutes = parseInt(timeParts[2], 10);
  const ap = timeParts[3].toUpperCase();

  hours = hours % 12;
  if (ap === "PM") hours += 12;

  return hours * 60 + minutes;
};

export default function SectionFrontTable<T>(props: SectionFrontTableProps<T>) {
  const {
    columns,
    rows,
    className,
    sectionHeader,
    sectionUrl,
    subtitle,
    title,
    disclaimer,
  } = props;

  const [isHydrated, setIsHydrated] = useState(false);
  const [sortConfig, setSortConfig] = useState<TableColumnSortConfig<T>>({
    key: null,
    direction: null,
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getRowKey = useCallback((row: T): string | number => {
    const id = (row as Record<string, unknown>).id;

    if (typeof id === "number") return id;
    if (id) return String(id);

    return JSON.stringify(row);
  }, []);

  const handleSort = useCallback(
    (columnKey: keyof T) => {
      const currentCol = columns.find((col) => col.key === columnKey);

      if (currentCol) {
        const eventLabel = `sort_by_${currentCol.key
          .toString()
          .toLowerCase()
          .replace(/ /g, "_")}`;

        trackClickEvent("TableSort", eventLabel, "click", "Market Data");
      }

      setSortConfig((current) => {
        if (current.key !== columnKey) {
          return { key: columnKey, direction: "asc" };
        }

        if (current.direction === "asc") {
          return { key: columnKey, direction: "desc" };
        }

        if (current.direction === "desc") {
          return { key: columnKey, direction: "asc" };
        }

        return { key: null, direction: null };
      });

      addPageAction("SectionFrontTableSortClick", { columnName: columnKey });
    },
    [columns]
  );

  // TODO: data should be coming in raw and formatted in the view
  //  the helper functions won't be needed for sorting
  const sortedRows = useMemo(() => {
    // Don't apply sorting until after hydration to prevent server/client mismatch
    if (!isHydrated) return rows;

    const { key, direction } = sortConfig;

    if (!key || !direction) return rows;

    const currentCol = columns.find((col) => col.key === key);
    const columnType = currentCol?.options?.columnType;

    // use backup key when the main key might not exist in the data
    // e.g. municipalYieldChange1Day might not exist, so use priceChange1Day instead
    const columnKey = (currentCol?.options?.backupKey as keyof T) ?? key;

    return [...rows].sort((a, b) => {
      const aVal = a[columnKey];
      const bVal = b[columnKey];

      if (aVal === null) return 1;
      if (bVal === null) return -1;
      let compare: number;

      if (columnType === "number") {
        const aNum = toNum(aVal);
        const bNum = toNum(bVal);

        compare = aNum - bNum;
      } else if (columnType === "time") {
        const aMin = toMinutesSinceMidnight(aVal);
        const bMin = toMinutesSinceMidnight(bVal);

        compare = aMin - bMin;
      } else if (columnType === "currency") {
        const aCurr = a["id" as keyof T];
        const bCurr = b["id" as keyof T];

        compare = String(aCurr).localeCompare(String(bCurr));
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        compare = aVal - bVal;
      } else {
        compare = String(aVal).localeCompare(String(bVal));
      }

      return direction === "asc" ? compare : -compare;
    });
  }, [columns, isHydrated, rows, sortConfig]);

  return (
    <div className={styles.sectionFrontTable}>
      {title && (
        <div className={styles.titleContainer}>
          <div className={styles.titleWrapper}>
            <h2
              className={classes([
                styles.title,
                typographyStyles.coreHeadlineSm,
              ])}
            >
              {sectionUrl ? (
                <Link className={styles.sectionLink} href={sectionUrl}>
                  <LocalizedText value={title} />
                </Link>
              ) : (
                <span>
                  <LocalizedText value={title} />
                </span>
              )}
            </h2>{" "}
            <h5 className={styles.subtitle}>
              <LocalizedText value={subtitle} />
            </h5>
          </div>
        </div>
      )}
      {/* Optional section header */}
      {sectionHeader}
      <div className={styles.scroll}>
        <Table className={classes([className, styles.table])}>
          <TableHeader className={styles.header}>
            {columns.map((column, index) => (
              <TableColumn
                key={column.key as string}
                column={column}
                className={
                  index === 0
                    ? createClasses([
                        styles.stickyCol,
                        column.options?.disableQuickAdd
                          ? styles.quickAddAbsent
                          : styles.quickAddPresent,
                      ])
                    : undefined
                }
                sortState={
                  sortConfig.key === column.key ? sortConfig.direction : null
                }
                onSort={
                  column.allowSorting ? () => handleSort(column.key) : undefined
                }
              >
                <span className={typographyStyles.coreShortTextXs}>
                  <LocalizedText value={column.title} />{" "}
                </span>
              </TableColumn>
            ))}
          </TableHeader>
          <TableBody>
            {sortedRows.map((row) => (
              <TableRow
                key={getRowKey(row)}
                rowData={row}
                columns={columns}
                className={styles.row}
              >
                {columns.map((column, index) => (
                  <TableCell
                    key={column.key}
                    rowData={row}
                    column={column}
                    className={
                      index === 0
                        ? createClasses([
                            styles.stickyCol,
                            column.options?.disableQuickAdd &&
                              styles.quickAddAbsent,
                          ])
                        : undefined
                    }
                  />
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {disclaimer && (
        <div className={styles.sectionNote}>
          <div className={styles.tableDisclaimer}>{disclaimer}</div>
        </div>
      )}
    </div>
  );
}
